import Cookies from 'js-cookie';
import { readMarketingConsent, saveMarketingConsent } from './marketing-consent';
import { MARKETING_TAGS } from './marketing-tags';
import { CONVERSION_PAGE_REPORTED_STORAGE } from '../../../utilities/ad-conversions/conversion-page-reported-storage';

/**
 * Withdrawal (see CONTEXT.md): the visitor takes back Marketing Consent.
 *
 * A loaded tag cannot be unloaded, and one still running writes its cookies
 * again as the page unloads, so clearing them first would not last. Instead
 * the choice is saved and the page reloads without any tag; the fresh page
 * then clears what every Marketing Tag stores, with nothing left to write it
 * back. No vendor's own withdrawal call is used: Meta's and Google's delete
 * nothing, and OpenAI's stores a denial that would silence its pixel if the
 * visitor accepted again.
 *
 * Another tab's tags keep running until that tab navigates, and may write
 * their cookies again, even as it closes. So the clearing repeats on every
 * page load while the Withdrawal stands, until consent is given again.
 */

/**
 * localStorage key: this visitor withdrew on this site and has not given
 * consent since. localStorage, not sessionStorage, so every tab sees it.
 */
const WITHDRAWN_MARKER = 'marketing-consent-withdrawn';

const TAGS = Object.values(MARKETING_TAGS);

const matches = (pattern: string, name: string) =>
  pattern.endsWith('*') ? name.startsWith(pattern.slice(0, -1)) : name === pattern;

const matchesAny = (patterns: readonly string[], name: string) =>
  patterns.some((pattern) => matches(pattern, name));

/**
 * The host and every parent domain, longest first: `www.example.co.uk` gives
 * `www.example.co.uk`, `example.co.uk` and `co.uk`. The browser ignores a
 * deletion on a public suffix like `co.uk`, so no list of suffixes is needed.
 */
const cookieDomains = (hostname: string) => {
  const labels = hostname.split('.');
  return labels.slice(0, -1).map((_, i) => labels.slice(i).join('.'));
};

const deleteCookie = (name: string) => {
  Cookies.remove(name, { path: '/' });
  for (const domain of cookieDomains(window.location.hostname)) {
    Cookies.remove(name, { path: '/', domain });
  }
};

const clearStorage = (getStorage: () => Storage, patterns: readonly string[]) => {
  try {
    const storage = getStorage();
    const keys = Array.from({ length: storage.length }, (_, i) => storage.key(i));
    for (const key of keys) {
      if (key !== null && matchesAny(patterns, key)) storage.removeItem(key);
    }
  } catch {
    // Storage blocked (privacy settings, sandboxed frame): no tag could have stored anything there.
  }
};

/**
 * Deletes what every Marketing Tag stores, whether or not the tenant still
 * runs it, and the site's own record of the Conversion Pages it reported to
 * them, which holds the ids it sent.
 */
export const clearMarketingTagStorage = () => {
  const cookies = TAGS.flatMap((tag) => tag.stores.cookies);
  for (const name of Object.keys(Cookies.get())) {
    if (matchesAny(cookies, name)) deleteCookie(name);
  }
  clearStorage(() => localStorage, TAGS.flatMap((tag) => tag.stores.localStorage ?? []));
  clearStorage(() => sessionStorage, [
    ...TAGS.flatMap((tag) => tag.stores.sessionStorage ?? []),
    CONVERSION_PAGE_REPORTED_STORAGE,
  ]);
};

/** A full page load of `url`, or of this page: the only way to drop tags already running. */
export const loadPageAfresh = (url?: string) => {
  if (url) {
    window.location.assign(url);
  } else {
    window.location.reload();
  }
};

/** Saves the refusal, then loads `url` (or this page) afresh, which clears what the tags stored. */
export const withdrawMarketingConsent = (url?: string) => {
  saveMarketingConsent('declined');
  try {
    localStorage.setItem(WITHDRAWN_MARKER, '1');
  } catch {
    // No storage to carry the marker: clear now, at the risk of a
    // still-running tag writing a cookie back before the page goes.
    clearMarketingTagStorage();
  }
  loadPageAfresh(url);
};

/**
 * On every page load: while a Withdrawal stands, clear what the Marketing
 * Tags stored. Once consent has been given again, the Withdrawal is over and
 * what the tags store is theirs to keep.
 */
export const upholdWithdrawal = () => {
  try {
    if (localStorage.getItem(WITHDRAWN_MARKER) === null) return;
    if (readMarketingConsent() === 'given') {
      localStorage.removeItem(WITHDRAWN_MARKER);
      return;
    }
  } catch {
    return;
  }
  clearMarketingTagStorage();
};
